import LogoutIcon from '@mui/icons-material/Logout';
import SettingsIcon from '@mui/icons-material/Settings';
import {
  Avatar,
  Box,
  Collapse,
  Divider,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
} from '@mui/material';
import React, { ReactNode, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';

import sunnyLogo from '../../assets/sunny-logo.png';
import { ChartPieSliceIcon } from '../../components/atoms/icons/ChartPieSliceIcon';
import { ChevronUpIcon } from '../../components/atoms/icons/ChevronUpIcon';
import { GearSixIcon } from '../../components/atoms/icons/GearSixIcon';
import { NotesIcon } from '../../components/atoms/icons/NotesIcon';
import { ShieldCheckIcon } from '../../components/atoms/icons/ShieldCheckIcon';
import theme from '../../theme';
import { usePrototype } from './PrototypeContext';
import PrototypeDisclaimer from './PrototypeDisclaimer';

interface PrototypeSidebarProps {
  children: ReactNode;
}

const SIDEBAR_WIDTH = 260;

interface MenuItemConfig {
  label: string;
  icon: React.ReactNode;
  path?: string;
  children?: { label: string; path: string }[];
}

const menuConfig: MenuItemConfig[] = [
  {
    label: 'Analytics',
    icon: <ChartPieSliceIcon sx={{ width: 24, height: 24 }} />,
    children: [
      { label: 'Overview', path: '/prototype/analytics/overview' },
      { label: 'Activation', path: '/prototype/analytics/activation' },
      { label: 'Actions', path: '/prototype/analytics/actions' },
      { label: 'Cards', path: '/prototype/analytics/cards' },
      { label: 'Members', path: '/prototype/analytics/members' },
    ],
  },
  {
    label: 'Content',
    icon: <NotesIcon sx={{ width: 24, height: 24 }} />,
    children: [
      { label: 'Cards', path: '/prototype/content/cards' },
      { label: 'FAQs', path: '/prototype/content/faqs' },
      { label: 'Agreements', path: '/prototype/content/agreements' },
    ],
  },
  {
    label: 'Actions',
    icon: <ShieldCheckIcon sx={{ width: 24, height: 24 }} />,
    path: '/prototype/actions',
  },
  {
    label: 'Questionnaire',
    icon: <NotesIcon sx={{ width: 24, height: 24 }} />,
    path: '/prototype/questionnaire',
  },
  {
    label: 'Cohorts',
    icon: <GearSixIcon sx={{ width: 24, height: 24 }} />,
    path: '/prototype/cohorts',
  },
  {
    label: 'Customizations',
    icon: <GearSixIcon sx={{ width: 24, height: 24 }} />,
    path: '/prototype/customizations',
  },
  {
    label: 'Tenant Settings',
    icon: <GearSixIcon sx={{ width: 24, height: 24 }} />,
    path: '/prototype/tenant-settings',
  },
];

const PrototypeSidebar: React.FC<PrototypeSidebarProps> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { selectedTenant, setSelectedTenant, selectedYear, setSelectedYear } = usePrototype();
  const [expandedMenus, setExpandedMenus] = useState<string[]>(['Analytics']);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleMenuToggle = (label: string) => {
    setExpandedMenus((prev) =>
      prev.includes(label) ? prev.filter((l) => l !== label) : [...prev, label]
    );
  };

  const handleNavigate = (path: string) => {
    navigate(path);
  };

  const isActive = (path: string) => location.pathname === path;
  const isParentActive = (children?: { path: string }[]) =>
    children?.some((child) => location.pathname === child.path);

  const handleUserMenuClick = (e: React.MouseEvent<HTMLElement>) => setAnchorEl(e.currentTarget);
  const handleUserMenuClose = () => setAnchorEl(null);

  return (
    <Box
      sx={{
        display: 'flex',
        minHeight: '100vh',
        bgcolor: '#F7F5F2',
        fontFamily: 'Manrope, sans-serif',
      }}
    >
      <Drawer
        variant="permanent"
        sx={{
          width: SIDEBAR_WIDTH,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: SIDEBAR_WIDTH,
            boxSizing: 'border-box',
            bgcolor: '#FFFFFF',
            borderRight: '1px solid #EFF0F0',
            fontFamily: 'Manrope, sans-serif',
          },
        }}
      >
        {/* Logo Section */}
        <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box
            component="img"
            src={sunnyLogo}
            alt="Sunny Benefits"
            sx={{ width: 40, height: 40 }}
          />
          <Typography variant="subtitle1" fontWeight={600}>
            Sunny Benefits
          </Typography>
        </Box>

        {/* Dropdowns */}
        <Box sx={{ px: 2, pb: 2, display: 'flex', gap: 1 }}>
          <Select
            size="small"
            value={selectedTenant}
            onChange={(e: SelectChangeEvent) => setSelectedTenant(e.target.value)}
            sx={{
              flex: 1,
              fontSize: 12,
              bgcolor: '#F7F5F2',
              '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
            }}
          >
            <MenuItem value="Master">Master</MenuItem>
            <MenuItem value="Kaiser Permanente">Kaiser Permanente</MenuItem>
            <MenuItem value="HAP">HAP</MenuItem>
            <MenuItem value="Watco">Watco</MenuItem>
            <MenuItem value="Navitus">Navitus</MenuItem>
          </Select>
          <Select
            size="small"
            value={selectedYear}
            onChange={(e: SelectChangeEvent) => setSelectedYear(e.target.value)}
            sx={{
              width: 80,
              fontSize: 12,
              bgcolor: '#F7F5F2',
              '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
            }}
          >
            <MenuItem value="2024">2024</MenuItem>
            <MenuItem value="2025">2025</MenuItem>
            <MenuItem value="2026">2026</MenuItem>
          </Select>
        </Box>

        <Divider sx={{ borderColor: '#F7F5F2' }} />

        {/* Menu Items */}
        <List sx={{ flex: 1, px: 1, py: 2 }}>
          {menuConfig.map((item) => (
            <React.Fragment key={item.label}>
              <ListItemButton
                onClick={() =>
                  item.children ? handleMenuToggle(item.label) : handleNavigate(item.path!)
                }
                sx={{
                  borderRadius: 2,
                  mb: 0.5,
                  bgcolor:
                    isActive(item.path || '') || isParentActive(item.children)
                      ? theme.palette.accentsYellowA.main
                      : 'transparent',
                  '&:hover': {
                    bgcolor: isActive(item.path || '')
                      ? theme.palette.accentsYellowA.main
                      : '#F7F5F2',
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: 40, color: theme.palette.textDarkPrimary.primary }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{ fontSize: 14, fontWeight: 500 }}
                />
                {item.children && (
                  <ChevronUpIcon
                    sx={{
                      width: 20,
                      height: 20,
                      transform: expandedMenus.includes(item.label)
                        ? 'rotate(0deg)'
                        : 'rotate(180deg)',
                      transition: 'transform 0.2s',
                    }}
                  />
                )}
              </ListItemButton>

              {item.children && (
                <Collapse in={expandedMenus.includes(item.label)} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding sx={{ pl: 2 }}>
                    {item.children.map((child) => (
                      <ListItemButton
                        key={child.label}
                        onClick={() => handleNavigate(child.path)}
                        sx={{
                          borderRadius: 2,
                          py: 1,
                          pl: 5,
                          bgcolor: isActive(child.path)
                            ? theme.palette.accentsYellowA.light
                            : 'transparent',
                          '&:hover': {
                            bgcolor: isActive(child.path)
                              ? theme.palette.accentsYellowA.light
                              : '#F7F5F2',
                          },
                        }}
                      >
                        <ListItemText
                          primary={child.label}
                          primaryTypographyProps={{
                            fontSize: 14,
                            fontWeight: isActive(child.path) ? 600 : 400,
                          }}
                        />
                      </ListItemButton>
                    ))}
                  </List>
                </Collapse>
              )}
            </React.Fragment>
          ))}
        </List>

        <Divider sx={{ borderColor: '#F7F5F2' }} />

        {/* Bottom Section */}
        <Box sx={{ p: 1 }}>
          <ListItemButton sx={{ borderRadius: 2 }}>
            <ListItemIcon sx={{ minWidth: 40 }}>
              <SettingsIcon sx={{ width: 24, height: 24 }} />
            </ListItemIcon>
            <ListItemText
              primary="Account Settings"
              primaryTypographyProps={{ fontSize: 14, fontWeight: 500 }}
            />
          </ListItemButton>
          <ListItemButton sx={{ borderRadius: 2 }}>
            <ListItemIcon sx={{ minWidth: 40 }}>
              <LogoutIcon sx={{ width: 24, height: 24 }} />
            </ListItemIcon>
            <ListItemText
              primary="Logout"
              primaryTypographyProps={{ fontSize: 14, fontWeight: 500 }}
            />
          </ListItemButton>
        </Box>

        <Divider sx={{ borderColor: '#F7F5F2' }} />

        {/* User Profile */}
        <Box
          sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1.5, cursor: 'pointer' }}
          onClick={handleUserMenuClick}
        >
          <Avatar sx={{ width: 40, height: 40, bgcolor: theme.palette.accentsYellowA.main }}>
            JM
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" fontWeight={600} noWrap>
              Jeremy M.
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap>
              demo@sunnybene...
            </Typography>
          </Box>
          <ChevronUpIcon sx={{ width: 20, height: 20 }} />
        </Box>

        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleUserMenuClose}>
          <MenuItem onClick={handleUserMenuClose}>Profile</MenuItem>
          <MenuItem onClick={handleUserMenuClose}>Settings</MenuItem>
          <MenuItem onClick={handleUserMenuClose}>Logout</MenuItem>
        </Menu>
      </Drawer>

      {/* Main Content */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <PrototypeDisclaimer />
        <Box sx={{ flex: 1, overflow: 'auto' }}>{children}</Box>
      </Box>
    </Box>
  );
};

export default PrototypeSidebar;
